// AdminDeepDiveModal — Feb 2026 · user request: "clicking (i) should
// show the same data output as clicking Deep Dive at the client end."
//
// Reuses the exact tab structure and section renderers as the client
// RefDeepDiveModal so admin + client see byte-identical evaluations.
// Fetches from `/api/viatris/assets/{ea_id}/deep-dive` (admin-auth)
// which delegates to the same client handler under the hood, keeping
// the payload shape 1:1.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  CitedText,
  SectionRefStrip,
  ReferenceRail,
  CompanyProfileBlock,
  CompetitionBlock,
  GenericSection,
} from '@/lib/deepDiveRender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_BADGE = {
  'Strategic Priority': 'bg-violet-100 text-violet-800 border-violet-300',
  'High Priority':      'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Medium':             'bg-sky-100 text-sky-800 border-sky-300',
  'Monitor':            'bg-amber-100 text-amber-800 border-amber-300',
  'Low Priority':       'bg-orange-100 text-orange-800 border-orange-300',
  'Not Suitable':       'bg-rose-100 text-rose-900 border-rose-400',
  'Could Not Assess':   'bg-slate-100 text-slate-700 border-slate-300',
};

const TABS = [
  { key: 'exec',    label: 'Executive Summary' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'company', label: 'Company' },
  { key: 'asset',   label: 'Asset' },
  { key: 'science', label: 'Science' },
  { key: 'comp',    label: 'Competition' },
];

function ExecBlock({ exec, refIds, onCite, maxRef }) {
  if (!exec || !Object.keys(exec).length) return <div className="text-slate-500 text-sm">No executive summary available.</div>;
  return (
    <div className="space-y-4">
      {exec.headline && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Headline</div>
          <p className="text-base text-slate-800 italic">
            &ldquo;<CitedText text={exec.headline} onCite={onCite} maxRef={maxRef} />&rdquo;
          </p>
        </div>
      )}
      {exec.rationale && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Rationale</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.rationale} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.why_relevant && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Why Relevant</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.why_relevant} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {Array.isArray(exec.key_strengths) && exec.key_strengths.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Key Strengths</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {exec.key_strengths.map((s, i) => <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>)}
          </ul>
        </div>
      )}
      {Array.isArray(exec.key_risks || exec.risks) && (exec.key_risks || exec.risks).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">Key Risks</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {(exec.key_risks || exec.risks).map((s, i) => <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>)}
          </ul>
        </div>
      )}
      {exec.scientific_differentiation && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Scientific Differentiation</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.scientific_differentiation} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.commercial_opportunity && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Commercial Opportunity</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.commercial_opportunity} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.strategic_fit_for_viatris && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Strategic Fit for Viatris</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.strategic_fit_for_viatris} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.recommended_next_action && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Recommended Next Action</div>
          <p className="text-sm text-slate-700">
            {exec.recommended_next_action}
            {exec.next_action_rationale && (
              <span className="block text-xs text-slate-500 mt-1">
                <CitedText text={exec.next_action_rationale} onCite={onCite} maxRef={maxRef} />
              </span>
            )}
          </p>
        </div>
      )}
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}

function ScoringBlock({ scoring, composite, weighted, versions, tierChange }) {
  if (!scoring || !Object.keys(scoring).length) return <div className="text-slate-500 text-sm">No scoring available.</div>;
  const dims = Object.entries(scoring).filter(([, v]) => v && typeof v === 'object' && 'score' in v);
  const fmt = (v) => (v && v.weighted_avg_score != null
    ? Number(v.weighted_avg_score).toFixed(2)
    : (v && v.composite_score != null ? (v.composite_score / 9).toFixed(2) : '—'));
  const tcChanged = tierChange && tierChange.from_tier && (
    tierChange.from_tier !== tierChange.to_tier ||
    (tierChange.from_score != null && tierChange.to_score != null && Math.abs(tierChange.from_score - tierChange.to_score) >= 0.01) ||
    (tierChange.dimension_deltas || []).length > 0
  );
  return (
    <div className="space-y-3">
      {/* Jun 2026 · Admin-only run history + what-changed comparison —
          the backend only sends these fields on the internal route. */}
      {Array.isArray(versions) && versions.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="version-history-strip">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Run history:</span>
          {versions.slice().reverse().map((v) => (
            <span
              key={v.version}
              className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${v.version === versions[0].version ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
              title={v.created_at ? new Date(v.created_at).toLocaleString() : ''}
            >
              v{v.version} · {fmt(v)} · {v.recommendation || '—'}{v.version === versions[0].version ? ' · current' : ''}
            </span>
          ))}
        </div>
      )}
      {tcChanged && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="what-changed-panel">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-800 mb-1">
            What changed in the latest run (v{tierChange.from_version} → v{tierChange.to_version})
          </div>
          <div className="text-xs text-amber-900 mb-1.5">
            Tier: <strong>{tierChange.from_tier}</strong> → <strong>{tierChange.to_tier}</strong>
            {tierChange.from_score != null && tierChange.to_score != null && (
              <> · Score: <strong>{Number(tierChange.from_score).toFixed(2)}</strong> → <strong>{Number(tierChange.to_score).toFixed(2)}</strong> /5</>
            )}
          </div>
          {(tierChange.dimension_deltas || []).length > 0 && (
            <ul className="list-disc pl-4 text-[11px] text-amber-900 space-y-1">
              {tierChange.dimension_deltas.map((d, i) => (
                <li key={i}>
                  <span className="capitalize font-semibold">{d.dimension.replace(/_/g, ' ')}</span>: {d.from} → {d.to}
                  {d.rationale ? ` — ${d.rationale}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex items-baseline gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Weighted Avg</div>
          <div className="text-2xl font-bold text-indigo-700">
            {weighted != null ? weighted.toFixed(2) : (composite != null ? (composite / 9).toFixed(2) : '—')}<span className="text-sm font-normal text-slate-500"> / 5</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {dims.map(([key, val]) => {
          const score = Number(val.score) || 0;
          const pct = (score / 5) * 100;
          return (
            <div key={key} className="border border-slate-100 rounded p-3 bg-slate-50/40">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-xs font-semibold text-slate-800 capitalize">{key.replace(/_/g, ' ')}</div>
                <div className="text-sm font-bold text-indigo-700">{score}<span className="text-[10px] font-normal text-slate-500"> / 5</span></div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
              </div>
              {val.rationale && <p className="text-[11px] text-slate-700 leading-relaxed">{val.rationale}</p>}
              {val.confidence && <div className="text-[10px] text-slate-500 mt-1">Confidence: <strong>{val.confidence}</strong></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Feb 2026 · direct escape hatch for the "cohort batch skipped this
// row" problem. When the Deep Dive modal renders with no assessment,
// let the user KICK OFF the per-asset AI pipeline right from the modal.
// Bypasses `reporting_cohort_name` matching entirely — hits
// `POST /viatris/assets/{ea_id}/generate-assessment` which does not
// require the row to belong to any cohort. Polls `/assessment-status`
// every 5s until done, then triggers the deep-dive refetch so the
// modal fills in-place.
function EmptyAssessmentBlock({ ea_id, displayName, message, onRefetch }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const startAssessment = async () => {
    setRunning(true);
    setStatus({ status: 'queued', progress_pct: 0, step_name: 'Queuing…' });
    try {
      await axios.post(
        `${API}/viatris/assets/${ea_id}/generate-assessment`,
        {},
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(`AI assessment queued for ${displayName}`);
      const t0 = Date.now();
      let last = null;
      while (Date.now() - t0 < 15 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const s = await axios.get(
            `${API}/viatris/assets/${ea_id}/assessment-status`,
            { withCredentials: true, timeout: 15000 },
          );
          last = s.data;
          setStatus(last);
          if (last?.status === 'complete' || last?.status === 'error' || last?.status === 'failed') break;
        } catch (_e) { /* transient */ }
      }
      if (last?.status === 'complete') {
        toast.success('Deep dive generated — reloading');
        onRefetch();
      } else if (last?.status === 'error' || last?.status === 'failed') {
        toast.error(`Assessment failed: ${last.error || 'unknown'}`);
      } else {
        toast.warning('Still running — refresh the modal in a couple minutes.');
      }
    } catch (e) {
      toast.error(`Failed to queue: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-4 py-6">
      <div className="text-slate-600 text-sm">
        {message || 'No AI assessment has been generated for this asset yet.'}
      </div>
      <Button
        onClick={startAssessment}
        disabled={running}
        className="bg-indigo-600 hover:bg-indigo-700 text-white"
        data-testid="empty-deep-dive-run-ai"
      >
        {running ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {status?.step_name || 'Running…'} · {status?.progress_pct || 0}%</>
        ) : (
          <><Sparkles className="w-4 h-4 mr-2" /> Run AI Assessment for this asset</>
        )}
      </Button>
      {running && (
        <div className="w-full max-w-md">
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all duration-500"
              style={{ width: `${status?.progress_pct || 0}%` }}
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Perplexity + Claude pipeline. Takes 30–120 seconds. Bypasses the cohort batch — targets this asset only.
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminDeepDiveModal({ ea_id, asset, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('exec');
  const [highlightN, setHighlightN] = useState(null);
  const onCite = (n) => setHighlightN(n);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/viatris/assets/${ea_id}/deep-dive`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load deep dive');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ea_id]);

  const refs = Array.isArray(data?.references) ? data.references : [];
  const refsBySection = data?.references_by_section || {};
  const maxRef = refs.length;

  const headerAsset = data || asset || {};
  const displayName = headerAsset.asset_name || asset?.asset_name || 'Asset';

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-deep-dive-backdrop"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="admin-deep-dive-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-lg text-slate-800 truncate" title={displayName}>{displayName}</h3>
              {data?.composite_score != null && (
                <span
                  className="inline-flex items-baseline gap-0.5 text-xs px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800"
                  title="Weighted average per scored dimension (1–5 scale)"
                >
                  <span className="font-bold">{(data.weighted_avg_score ?? data.composite_score / 9).toFixed(2)}</span>
                  <span className="text-[10px] opacity-70">/5</span>
                </span>
              )}
              {data?.recommendation && (
                <Badge className={`text-[10px] border ${TIER_BADGE[data.recommendation] || 'bg-slate-100 text-slate-700'}`}>
                  {data.recommendation}
                </Badge>
              )}
              {/* Feb 2026 · Manual override — escape hatch for stuck
                  Could Not Assess rows (user report: HH-120 stayed CNA
                  after multiple retries; Claude's scoring step returns
                  invalid JSON for some Chinese assets no matter how
                  many times we retry). Admin picks a tier → PATCH
                  /viatris/assets/{ea_id} → row exits CNA immediately
                  with a documented manual_override=true audit flag. */}
              <ManualOverrideMenu
                ea_id={ea_id}
                current={data?.recommendation}
                onSaved={(newRec) => {
                  toast.success(`Recommendation set to "${newRec}"`);
                  // Refetch the deep dive so the badge + tabs update.
                  setLoading(true);
                  axios.get(`${API}/viatris/assets/${ea_id}/deep-dive`, { withCredentials: true })
                    .then((r) => setData(r.data))
                    .catch(() => {})
                    .finally(() => setLoading(false));
                }}
              />
              {(() => {
                const weighted = data?.weighted_avg_score ?? (
                  data?.composite_score != null ? data.composite_score / 9 : null
                );
                if (weighted == null) return null;
                let word = 'Decline', cls = 'bg-rose-100 text-rose-900 border-rose-300';
                if (weighted >= 4.0) { word = 'In-License'; cls = 'bg-emerald-100 text-emerald-900 border-emerald-300'; }
                else if (weighted >= 3.0) { word = 'Reassess'; cls = 'bg-sky-100 text-sky-900 border-sky-300'; }
                return (
                  <Badge
                    className={`text-[11px] border font-bold ${cls}`}
                    title={`Committee weighted avg ${weighted.toFixed(2)}/5 · ≥ 4.0 In-License · ≥ 3.0 Reassess · < 3.0 Decline`}
                    data-testid="admin-hero-verdict-badge"
                  >
                    Verdict: {word}
                  </Badge>
                );
              })()}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {(asset?.indication || headerAsset.indication) || '—'} · {(asset?.phase || headerAsset.phase) || '—'} · {(asset?.modality || headerAsset.modality) || '—'} · {(headerAsset.biotech_name || asset?.company_name) || '—'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="admin-deep-dive-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3 border-b border-slate-200 flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-700 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`admin-deep-dive-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          <div className="flex-1 overflow-y-auto p-5 text-sm min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : error ? (
              <div className="text-rose-700 text-sm">{error}</div>
            ) : !data?.has_assessment ? (
              <EmptyAssessmentBlock
                ea_id={ea_id}
                displayName={displayName}
                message={data?.message}
                onRefetch={() => {
                  setLoading(true);
                  setError(null);
                  axios.get(`${API}/viatris/assets/${ea_id}/deep-dive`, { withCredentials: true })
                    .then((r) => setData(r.data))
                    .catch((e) => setError(e?.response?.data?.detail || 'Failed to load deep dive'))
                    .finally(() => setLoading(false));
                }}
              />
            ) : (
              <>
                {tab === 'exec'    && <ExecBlock    exec={data.executive_summary} refIds={refsBySection.executive_summary} onCite={onCite} maxRef={maxRef} />}
                {tab === 'scoring' && <ScoringBlock scoring={data.scoring} composite={data.composite_score} weighted={data.weighted_avg_score} versions={data.versions} tierChange={data.tier_change} />}
                {tab === 'company' && <CompanyProfileBlock data={data.company_intelligence}  refIds={refsBySection.company_intelligence}  onCite={onCite} maxRef={maxRef} />}
                {tab === 'asset'   && <GenericSection title="Asset Intelligence"     data={data.asset_intelligence}    refIds={refsBySection.asset_intelligence}    onCite={onCite} maxRef={maxRef} />}
                {tab === 'science' && <GenericSection title="Scientific Assessment"  data={data.scientific_assessment} refIds={refsBySection.scientific_assessment} onCite={onCite} maxRef={maxRef} />}
                {tab === 'comp'    && <CompetitionBlock data={data.competitive_landscape} refIds={refsBySection.competitive_landscape} onCite={onCite} maxRef={maxRef} />}
              </>
            )}
          </div>
          {!loading && data?.has_assessment && tab !== 'scoring' && refs.length > 0 && (
            <div className="md:w-80 md:shrink-0 max-h-[45vh] md:max-h-none overflow-hidden md:border-t-0 border-t border-slate-200">
              <ReferenceRail refs={refs} highlightN={highlightN} onDismissHighlight={() => setHighlightN(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Feb 2026 · Manual Override menu — escape hatch for CNA rows.
// Saves via PATCH /viatris/assets/{ea_id} (already supports the
// recommendation field). Stamps `manual_override=true` so the audit
// trail shows the tier was operator-set, not LLM-derived.
function ManualOverrideMenu({ ea_id, current, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const TIERS = [
    'Strategic Priority', 'High Priority', 'Medium',
    'Monitor', 'Low Priority', 'Not Suitable',
  ];

  const save = async (rec) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/viatris/assets/${ea_id}`,
        {
          recommendation: rec,
          auto_classified: false,
          manual_override: true,
          off_strategy_reason: null,
        },
        { withCredentials: true },
      );
      setOpen(false);
      onSaved?.(rec);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold"
        title="Manually set the recommendation tier (bypasses AI scoring)"
        data-testid="manual-override-btn"
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Override'}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded shadow-lg py-1 min-w-[180px]"
          onMouseLeave={() => setOpen(false)}
          data-testid="manual-override-menu"
        >
          <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold px-3 py-1 border-b border-slate-100">
            Set recommendation
          </div>
          {TIERS.map((t) => (
            <button
              key={t}
              onClick={() => save(t)}
              disabled={saving || t === current}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40 ${t === current ? 'bg-slate-50 font-semibold' : ''}`}
              data-testid={`manual-override-${t.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {t}{t === current ? ' (current)' : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
